import React, { useMemo } from "react";
import "./StarFieldBackground.css";
import earthImg from "./earth-particle.svg";

function makeRng(seed) {
  let x = seed >>> 0;
  return () => {
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    return (x >>> 0) / 4294967296;
  };
}

function generatePositions(count, maxPx, rng, minDur, maxDur) {
  const items = [];
  for (let i = 0; i < count; i += 1) {
    items.push({
      x: Math.floor(rng() * maxPx),
      y: Math.floor(rng() * maxPx),
      opacity: 0.35 + rng() * 0.5,
      // Horizontal sway amplitude: 40–160px, left or right
      travelX: Math.floor(40 + rng() * 120) * (rng() > 0.5 ? 1 : -1),
      // Full-cycle duration (vertical speed) — varies per layer for parallax
      floatDuration: minDur + Math.floor(rng() * (maxDur - minDur)),
      // Spin speed
      spinDuration: 25 + Math.floor(rng() * 55),
      // Negative delay so each particle starts at a random point in its cycle
      delay: -(Math.floor(rng() * 60)),
    });
  }
  return items;
}

function StarFieldBackground() {
  const { small, medium, big } = useMemo(() => {
    const rng = makeRng(708197);
    const max = 2000;
    return {
      small: generatePositions(18, max, rng, 35, 65),
      medium: generatePositions(10, max, rng, 65, 105),
      big: generatePositions(5, max, rng, 105, 155),
    };
  }, []);

  const renderParticles = (positions, sizeClass) =>
    positions.flatMap((p, i) => {
      const particleStyle = {
        left: p.x,
        top: p.y,
        opacity: p.opacity,
        "--travel-x": `${p.travelX}px`,
        "--float-dur": `${p.floatDuration}s`,
        "--spin-dur": `${p.spinDuration}s`,
        "--delay": `${p.delay}s`,
      };
      const dupStyle = { ...particleStyle, top: p.y + 2000 };

      return [
        <img
          key={`${sizeClass}-${i}`}
          src={earthImg}
          className={`earth-particle earth-particle--${sizeClass}`}
          style={particleStyle}
          alt=""
          draggable={false}
        />,
        <img
          key={`${sizeClass}-dup-${i}`}
          src={earthImg}
          className={`earth-particle earth-particle--${sizeClass}`}
          style={dupStyle}
          alt=""
          draggable={false}
        />,
      ];
    });

  return (
    <div className="starfield" aria-hidden="true">
      <div className="starfield-bg" />
      <div className="starfield-layer">
        {renderParticles(small, "small")}
      </div>
      <div className="starfield-layer">
        {renderParticles(medium, "medium")}
      </div>
      <div className="starfield-layer">
        {renderParticles(big, "big")}
      </div>
    </div>
  );
}

export default StarFieldBackground;
